import { eventNames, useCoreEvents } from '@carvana/core-events';
import { useCallback, useContext } from 'react';
import { ToastActions } from '~/components/common/FavoritesToastContent/types';
import useDisplayFavoritesToast from '~/components/common/FavoritesToastContent/useDisplayFavoritesToast';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import { sendSourcedFavoriteMessageToNative } from '~/native/common/dispatchers';
import { removeFavorite } from '~/services/common';
import { logError } from '~/utilities/loggers';
import type { FavoritesArguments } from './types';

type LifeCycleFn = (args: FavoritesArguments) => void;

type Options = {
  beforeRemoveFavorite?: LifeCycleFn;
};

const dummyFn = () => {};

export default function useRemoveFavorite({
  beforeRemoveFavorite = dummyFn
}: Options = {}) {
  const {
    favorites: { setData, setLoading }
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();
  const { emit } = useCoreEvents();
  const displayFavoritesToast = useDisplayFavoritesToast();

  return useCallback(
    async (args: FavoritesArguments) => {
      const { nativeEventSource, stockNumber, vehicleId } = args;

      if (!isLoggedIn) return;

      try {
        beforeRemoveFavorite(args);

        setLoading();

        const {
          data: { content: vehicles }
        } = await removeFavorite(vehicleId);

        if (nativeEventSource)
          sendSourcedFavoriteMessageToNative(
            'remove',
            {
              stockNumber: stockNumber ?? 0,
              vehicleId: vehicleId
            },
            nativeEventSource
          );

        setData(vehicles);

        emit(eventNames.favorites.mfe.refresh, { source: 'app', vehicles });
        displayFavoritesToast(ToastActions.remove);
      } catch (err) {
        logError(err, { location: 'Remove favorite' });
      }
    },
    [
      beforeRemoveFavorite,
      displayFavoritesToast,
      emit,
      isLoggedIn,
      setData,
      setLoading
    ]
  );
}
